/**
 * Search Customer Modal
 * Presentation component for searching and selecting customers
 * Follows Clean Architecture - uses domain entities and hooks
 */

import React, { useState, useCallback, useRef, useEffect } from "react";
import { Avatar } from "../../../../ui/components/Avatar";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { Table } from "../../../../ui/components/Table";
import { TextField } from "../../../../ui/components/TextField";
import { DialogLayout } from "../../../../ui/layouts/DialogLayout";
import { FeatherCheck } from "@subframe/core";
import { FeatherSearch } from "@subframe/core";
import { FeatherX } from "@subframe/core";
import { useNavigate, useLocation } from "react-router-dom";
import { useCustomers } from "../hooks/useCustomers";
import { CustomerSearchItemEntity } from "../../domain/entities/CustomerEntity";
import { InvoiceDataEntity } from "../../domain/entities/InvoiceEntity";
import { CREATE_MANUAL_INVOICE_RETURN_TO } from "../utils/manualInvoiceCustomerPatch";

interface SearchCustomerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPartySelect: (party: any) => void;
  id?: string;
  initialSearchTerm?: string;
  initialSelectedCustomerId?: string;
  /** When set, Add/Edit customer navigates back to this path with draft + optional customer patch (no sessionStorage). */
  createManualInvoiceFlow?: {
    returnPath: string;
    invoiceDraft: InvoiceDataEntity;
  };
}

const SearchCustomerModal: React.FC<SearchCustomerModalProps> = ({
  open,
  onOpenChange,
  onPartySelect,
  id,
  initialSearchTerm,
  initialSelectedCustomerId,
  createManualInvoiceFlow,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedParty, setSelectedParty] =
    useState<CustomerSearchItemEntity | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [isInitialSearch, setIsInitialSearch] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Use Clean Architecture hook
  const { customers, loading, error, fetchCustomers, clearError, reset } =
    useCustomers();

  const performSearch = useCallback(
    async (term: string) => {
      const trimmedSearch = term.trim();
      if (!trimmedSearch || trimmedSearch.length < 2) return;

      // Use invoice-specific vector similarity search
      await fetchCustomers(trimmedSearch);
      setHasSearched(true);
      setIsInitialSearch(false); // Initial search completed, allow typing
    },
    [fetchCustomers],
  );

  const idsMatch = useCallback(
    (left?: string | number | null, right?: string | number | null) => {
      if (
        left === undefined ||
        left === null ||
        right === undefined ||
        right === null
      ) {
        return false;
      }
      return String(left).trim() === String(right).trim();
    },
    [],
  );

  const isCustomerSelected = useCallback(
    (customer: { id: number | string; zoho_customerid?: string }) => {
      if (!selectedParty) return false;
      return (
        idsMatch(selectedParty.customer_id, customer.id) ||
        idsMatch(selectedParty.zoho_customerid, customer.zoho_customerid)
      );
    },
    [idsMatch, selectedParty],
  );

  // Handle modal reopen after add/edit (keeps modal open and refreshes search)
  useEffect(() => {
    if (location.state?.reopenSearchModal && location.state?.searchTerm) {
      // Reopen the modal
      onOpenChange(true);
      setSearchTerm(location.state.searchTerm);
      setIsInitialSearch(true); // Mark as initial search
      performSearch(location.state.searchTerm);
      // Clear the state
      navigate(".", { replace: true, state: {} });
    }
  }, [location.state, navigate, performSearch, onOpenChange]);

  // Auto-search when modal opens; prefer searching by ID if already selected in invoice JSON
  useEffect(() => {
    const runInitialSearch = async () => {
      if (!open) {
        return;
      }

      const selectedCustomerId = initialSelectedCustomerId?.trim();
      const trimmedInitialTerm = initialSearchTerm?.trim();

      if (selectedCustomerId) {
        setSearchTerm(initialSearchTerm || "");
        setIsInitialSearch(true);
        await fetchCustomers("", { customerId: selectedCustomerId, limit: 10 });
        setHasSearched(true);
        setIsInitialSearch(false);
        return;
      }

      if (trimmedInitialTerm) {
        setSearchTerm(initialSearchTerm || "");
        setIsInitialSearch(true);
        await performSearch(trimmedInitialTerm);
      }
    };

    runInitialSearch();
  }, [
    fetchCustomers,
    initialSearchTerm,
    initialSelectedCustomerId,
    open,
    performSearch,
  ]);

  // Auto-select customer when search results come back if initialSelectedCustomerId is provided
  useEffect(() => {
    if (initialSelectedCustomerId && customers.length > 0 && !selectedParty) {
      const customerToSelect = customers.find(
        (c) =>
          idsMatch(c.zoho_customerid, initialSelectedCustomerId) ||
          idsMatch(c.customer_id, initialSelectedCustomerId),
      );
      if (customerToSelect) {
        setSelectedParty(customerToSelect);
      }
    }
  }, [customers, idsMatch, initialSelectedCustomerId, selectedParty]);

  const handleClose = () => {
    onOpenChange(false);
    setSearchTerm("");
    setSelectedParty(null);
    setHasSearched(false);
    setIsInitialSearch(false);
    reset();
    clearError();
  };

  const handlePartySelect = (party: CustomerSearchItemEntity) => {
    setSelectedParty(party);
  };

  const handleConfirm = () => {
    if (selectedParty) {
      // Pass customer data along with party selection
      onPartySelect({
        ...selectedParty,
        name: selectedParty.customer_name,
        zoho_customerid: selectedParty.zoho_customerid,
        address: selectedParty.billing_address,
        gst_treatment: selectedParty.gst_treatment,
        gstin: selectedParty.gstin_vat_tax_id,
        status: selectedParty.status === "live" ? "Verified" : "Pending",
      });
      handleClose();
    }
  };

  // Debounced search function
  const debouncedSearch = useCallback(
    (searchValue: string) => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(async () => {
        if (searchValue.trim() && searchValue.trim().length >= 2) {
          // Use invoice-specific vector similarity search
          await fetchCustomers(searchValue.trim());
          setHasSearched(true);
          setIsInitialSearch(false); // Allow typing after search completes
        } else {
          reset();
          setHasSearched(false);
          setSelectedParty(null);
        }
      }, 300); // 300ms debounce
    },
    [fetchCustomers, reset],
  );

  const handleSearch = async () => {
    const trimmedSearch = searchTerm.trim();

    if (!trimmedSearch) {
      return;
    }

    if (trimmedSearch.length < 2) {
      return;
    }

    await performSearch(trimmedSearch);
  };

  const handleSearchInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    setSearchTerm(value);

    // Clear error when user starts typing again
    if (error) {
      clearError();
    }

    debouncedSearch(value);
  };

  // Helper function to format text (capitalize first letter, remove underscores)
  const formatText = (text: string): string => {
    if (!text) return "";
    return text
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Convert customers to display format
  const displayCustomers = (customers || []).map((customer) => {
    const formatted = formatText(customer.gst_treatment);

    return {
      id: customer.customer_id,
      zoho_customerid: customer.zoho_customerid,
      name: customer.customer_name,
      customerType: customer.customer_type,
      taxId: customer.gstin_vat_tax_id,
      address: customer.billing_address || "Address not available",
      status: customer.status === "live" ? "Verified" : "Pending",
      gstTreatment: customer.gst_treatment,
      gstTreatmentFormatted: formatted,
      gstCustomerType: customer.gst_customertype,
      email: customer.contact_email_1,
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(
        customer.customer_name,
      )}&background=random`,
    };
  });

  return (
    <DialogLayout open={open} onOpenChange={onOpenChange}>
      <div className="flex w-320 flex-col items-start gap-6 bg-default-background px-8 py-6">
        <div className="flex w-full items-center justify-between">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Search Customer
          </span>
          <IconButton icon={<FeatherX />} onClick={handleClose} />
        </div>
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-96">
            <span className="text-body-bold font-body-bold text-default-font">
              Search Existing Customer
            </span>
          </div>
        </div>
        <div className="flex w-full flex-col items-start gap-4">
          <div className="flex w-full items-center gap-4">
            <TextField
              className="grow"
              variant="filled"
              label=""
              helpText=""
              icon={<FeatherSearch />}
            >
              <TextField.Input
                placeholder="Search by name, tax ID, or address..."
                value={searchTerm}
                onChange={handleSearchInputChange}
                disabled={loading && isInitialSearch}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !(loading && isInitialSearch)) {
                    handleSearch();
                  }
                }}
              />
            </TextField>
            <Button
              variant="brand-primary"
              icon={<FeatherSearch />}
              onClick={handleSearch}
              disabled={!searchTerm.trim() || loading}
              loading={loading}
            >
              Search
            </Button>
            {/* <Button
              variant="neutral-tertiary"
              iconRight={<FeatherChevronDown />}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {}}
            >
              Filters
            </Button> */}
          </div>
          {loading ? (
            <div className="flex w-full items-center justify-center py-12">
              <div className="flex flex-col items-center gap-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-neutral-100">
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-300 border-t-brand-primary"></div>
                </div>
                <span className="text-body font-body text-neutral-500">
                  Searching customers...
                </span>
              </div>
            </div>
          ) : error ? (
            <div className="flex w-full items-center justify-center py-12">
              <div className="flex flex-col items-center gap-4">
                <span className="text-body font-body text-red-500">
                  {error}
                </span>
                <Button onClick={handleSearch}>Retry</Button>
              </div>
            </div>
          ) : !hasSearched ? (
            <div className="flex w-full items-center justify-center py-12">
              <div className="flex flex-col items-center gap-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-neutral-100">
                  <FeatherSearch className="h-8 w-8 text-neutral-400" />
                </div>
                <span className="text-body font-body text-neutral-500">
                  Enter a search term to find customers
                </span>
              </div>
            </div>
          ) : displayCustomers.length === 0 ? (
            <div className="flex w-full items-center justify-center py-12">
              <div className="flex flex-col items-center gap-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-neutral-100">
                  <FeatherSearch className="h-8 w-8 text-neutral-400" />
                </div>
                <span className="text-body font-body text-neutral-500">
                  No customers found for "{searchTerm}"
                </span>
              </div>
            </div>
          ) : (
            <Table
              header={
                <Table.HeaderRow>
                  <Table.HeaderCell className="h-8 w-96 flex-none">
                    Customer Name
                  </Table.HeaderCell>
                  <Table.HeaderCell className="h-8 w-40 flex-none">
                    GSTIN
                  </Table.HeaderCell>

                  <Table.HeaderCell className="h-8 w-24 flex-none">
                    Status
                  </Table.HeaderCell>
                  <Table.HeaderCell className="h-8 w-40 flex-none">
                    Actions
                  </Table.HeaderCell>
                </Table.HeaderRow>
              }
            >
              {displayCustomers.map((customer) => (
                <Table.Row key={customer.id}>
                  <Table.Cell className="w-96 flex-none">
                    <div className="flex items-start gap-3">
                      <Avatar
                        size="small"
                        image={customer.avatar}
                        square={true}
                      >
                        {customer.name.charAt(0)}
                      </Avatar>
                      <div className="flex flex-col flex-1 min-w-0">
                        <span className="text-body-bold font-body-bold text-default-font break-words">
                          {customer.name}
                        </span>
                        <span className="text-caption font-caption text-neutral-500 break-words">
                          {customer.gstTreatmentFormatted ||
                            customer.gstTreatment ||
                            "No GST Treatment"}
                        </span>
                      </div>
                    </div>
                  </Table.Cell>
                  <Table.Cell className="w-40 flex-none">
                    <span className="text-body font-body text-default-font">
                      {customer.taxId}
                    </span>
                  </Table.Cell>

                  <Table.Cell className="w-24 flex-none">
                    <Badge
                      variant={
                        customer.status === "Verified" ? "success" : "warning"
                      }
                      icon={
                        customer.status === "Verified" ? (
                          <FeatherCheck />
                        ) : undefined
                      }
                    >
                      {customer.status}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell className="w-40 flex-none">
                    <div className="flex items-center gap-2">
                      <Button
                        size="small"
                        onClick={() => {
                          const selectedCustomer = (customers || []).find(
                            (c) =>
                              idsMatch(c.customer_id, customer.id) ||
                              idsMatch(
                                c.zoho_customerid,
                                customer.zoho_customerid,
                              ),
                          );
                          if (selectedCustomer) {
                            handlePartySelect(selectedCustomer);
                          }
                        }}
                        variant={
                          isCustomerSelected(customer)
                            ? "brand-primary"
                            : "neutral-secondary"
                        }
                      >
                        {isCustomerSelected(customer) ? "Selected" : "Select"}
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table>
          )}
        </div>
        <div className="flex w-full items-center justify-end gap-2 pt-6">
          <Button variant="neutral-tertiary" onClick={handleClose}>
            Cancel
          </Button>
          <Button disabled={!selectedParty} onClick={handleConfirm}>
            Confirm Customer
          </Button>
        </div>
      </div>
    </DialogLayout>
  );
};

export default SearchCustomerModal;
